import { baseApiClient as api } from "../baseService";
export const addTagTypes = ["sms-templates"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      matterSmsTemplateContextual: build.query<
        MatterSmsTemplateContextualApiResponse,
        MatterSmsTemplateContextualApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/sms-template/`,
          params: {
            category_override: queryArg.categoryOverride,
          },
        }),
        providesTags: ["sms-templates"],
      }),
      smsTemplatePreview: build.query<
        SmsTemplatePreviewApiResponse,
        SmsTemplatePreviewApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/sms-template/preview/`,
          params: {
            template_id: queryArg.templateId,
          },
        }),
        providesTags: ["sms-templates"],
      }),
      globalSmsTemplatesList: build.query<
        GlobalSmsTemplatesListApiResponse,
        GlobalSmsTemplatesListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/sms-templates/global/`,
          params: {
            active_only: queryArg.activeOnly,
            category: queryArg.category,
          },
        }),
        providesTags: ["sms-templates"],
      }),
      globalSmsTemplatesCreate: build.mutation<
        GlobalSmsTemplatesCreateApiResponse,
        GlobalSmsTemplatesCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/sms-templates/global/`,
          method: "POST",
          body: queryArg.createSmsTemplateRequest,
        }),
        invalidatesTags: ["sms-templates"],
      }),
      globalSmsTemplatesRetrieve: build.query<
        GlobalSmsTemplatesRetrieveApiResponse,
        GlobalSmsTemplatesRetrieveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/sms-templates/global/${queryArg.id}/`,
        }),
        providesTags: ["sms-templates"],
      }),
      globalSmsTemplatesPartialUpdate: build.mutation<
        GlobalSmsTemplatesPartialUpdateApiResponse,
        GlobalSmsTemplatesPartialUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/sms-templates/global/${queryArg.id}/`,
          method: "PATCH",
          body: queryArg.patchedUpdateSmsTemplateRequest,
        }),
        invalidatesTags: ["sms-templates"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type MatterSmsTemplateContextualApiResponse =
  /** status 200  */ ContextualSmsTemplateResponse[];
export type MatterSmsTemplateContextualApiArg = {
  /** Override the inferred category with a specific one */
  categoryOverride?:
    | "Document Collection"
    | "Initial Treatment Assessment"
    | "Ongoing Treatment Check-In";
  matterId: string;
};
export type SmsTemplatePreviewApiResponse =
  /** status 200  */ MergeFieldPreviewResponse;
export type SmsTemplatePreviewApiArg = {
  /** The ID of the matter to use for merge field values */
  matterId: string;
  /** The ID of the template to preview */
  templateId: string;
};
export type GlobalSmsTemplatesListApiResponse =
  /** status 200  */ SmsTemplateListResponse[];
export type GlobalSmsTemplatesListApiArg = {
  /** Only return active templates (default: true) */
  activeOnly?: boolean;
  /** Filter templates by category */
  category?:
    | "Document Collection"
    | "Initial Treatment Assessment"
    | "Ongoing Treatment Check-In";
};
export type GlobalSmsTemplatesCreateApiResponse =
  /** status 201  */ SmsTemplateSchema;
export type GlobalSmsTemplatesCreateApiArg = {
  createSmsTemplateRequest: CreateSmsTemplateRequest;
};
export type GlobalSmsTemplatesRetrieveApiResponse =
  /** status 200  */ SmsTemplateSchema;
export type GlobalSmsTemplatesRetrieveApiArg = {
  id: string;
};
export type GlobalSmsTemplatesPartialUpdateApiResponse =
  /** status 200  */ SmsTemplateSchema;
export type GlobalSmsTemplatesPartialUpdateApiArg = {
  id: string;
  patchedUpdateSmsTemplateRequest: UpdateSmsTemplateRequest;
};
export type SmsTemplateCategory =
  | "Document Collection"
  | "Initial Treatment Assessment"
  | "Ongoing Treatment Check-In";
export type SmsTemplateSchema = {
  id: string | string;
  created_at: string;
  modified_at: string;
  /** The firm this template belongs to. Null for global templates. */
  firm_id?: (string | string) | null;
  /** Human-readable name for the template */
  name: string;
  /** The communication stage this template is for */
  category: SmsTemplateCategory;
  /** Template content with {{merge_fields}} placeholders */
  content: string;
  /** Whether this template is available for use */
  is_active: boolean;
  /** True for system-wide templates, false for firm-specific */
  is_global: boolean;
  /** Default template for its category (within firm or global scope). */
  is_default?: boolean;
  /** Lower numbers are preferred when selecting a recommended template. */
  priority?: number;
  /** Days since last client response this template targets. */
  cadence_day?: number | null;
  /** User who created this template. Null for seeded global templates. */
  created_by_id?: (string | string) | null;
};
export type ContextualSmsTemplateResponse = {
  /** Templates for the inferred category (firm or global set) */
  templates: SmsTemplateSchema[];
  /** Recommended template based on cadence day and priority */
  recommended_template?: SmsTemplateSchema;
  /** The category that was inferred based on matter state */
  inferred_category: SmsTemplateCategory;
  /** Human-readable explanation for why this category was selected */
  selection_reason: string;
  /** Pre-populated merge field values for preview */
  merge_field_context?: {
    [key: string]: string | null;
  };
  /** True if category_override was used instead of inference */
  was_overridden?: boolean;
};
export type MergeFieldPreviewResponse = {
  /** Original template content with {{merge_fields}} */
  original_content: string;
  /** Template content with merge fields replaced */
  rendered_content: string;
  /** The values used for each merge field */
  merge_field_values: {
    [key: string]: string | null;
  };
};
export type SmsTemplateListResponse = {
  /** List of SMS templates */
  templates: SmsTemplateSchema[];
  /** Total number of templates matching the query */
  total_count: number;
};
export type CreateSmsTemplateRequest = {
  /** Human-readable name for the template */
  name: string;
  /** The communication stage this template is for */
  category: SmsTemplateCategory;
  /** Template content with {{merge_fields}} placeholders */
  content: string;
  /** Whether this template is available for use */
  is_active?: boolean;
  /** Default template for its category (within firm or global scope). */
  is_default?: boolean;
  /** Lower numbers are preferred when selecting a recommended template. */
  priority?: number;
  /** Days since last client response this template targets. */
  cadence_day?: number | null;
};
export type UpdateSmsTemplateRequest = {
  /** Human-readable name for the template */
  name?: string | null;
  /** Template content with {{merge_fields}} placeholders */
  content?: string | null;
  /** Whether this template is available for use */
  is_active?: boolean | null;
  /** Default template for its category (within firm or global scope). */
  is_default?: boolean | null;
  /** Lower numbers are preferred when selecting a recommended template. */
  priority?: number | null;
  /** Days since last client response this template targets. */
  cadence_day?: number | null;
};
export const {
  useMatterSmsTemplateContextualQuery,
  useSmsTemplatePreviewQuery,
  useGlobalSmsTemplatesListQuery,
  useGlobalSmsTemplatesCreateMutation,
  useGlobalSmsTemplatesRetrieveQuery,
  useGlobalSmsTemplatesPartialUpdateMutation,
} = injectedRtkApi;
